import dayjs, { Dayjs } from 'dayjs';
import { changeTimeZone, translate } from '../../../helpers';
import { FooterButton } from '../types';

export type DateRange = [Date | null | undefined, Date | null | undefined];

const getStartOfToday = (timeZone?: string): Dayjs => {
  const date = timeZone ? dayjs(changeTimeZone(new Date(), timeZone)) : dayjs();
  return date.startOf('day');
};

export const getTodayRange = (timeZone?: string): DateRange => {
  const today = getStartOfToday(timeZone).toDate();
  return [today, today];
};

export const getYesterdayRange = (timeZone?: string): DateRange => {
  const yesterday = getStartOfToday(timeZone).subtract(1, 'days').toDate();
  return [yesterday, yesterday];
};

export const getNextXDaysRange = (days: number, timeZone?: string): DateRange => [
  getStartOfToday(timeZone).toDate(),
  getStartOfToday(timeZone).add(days, 'days').toDate(),
];

export const getPastXDaysRange = (days: number, timeZone?: string): DateRange => [
  getStartOfToday(timeZone).subtract(days, 'days').toDate(),
  getStartOfToday(timeZone).toDate(),
];

export const getOverdueRange = (timeZone?: string): DateRange => [
  null,
  getStartOfToday(timeZone).subtract(1, 'days').toDate(),
];

export const isSameRange = (
  [firstStartDate, firstEndDate]: DateRange,
  [secondStartDate, secondEndDate]: DateRange,
) => {
  const firstStartTime = firstStartDate?.getTime();
  const firstEndTime = firstEndDate?.getTime();

  const secondStartTime = secondStartDate?.getTime();
  const secondEndTime = secondEndDate?.getTime();

  return firstStartTime === secondStartTime && firstEndTime === secondEndTime;
};

export const getFooterButtonAttributes = (
  button: FooterButton,
): { label: string; getRange: (timeZone?: string) => DateRange } => {
  switch (button) {
    case FooterButton.Yesterday:
      return {
        label: translate('date.yesterday'),
        getRange: getYesterdayRange,
      };
    case FooterButton.Overdue:
      return {
        label: translate('date.overdue'),
        getRange: getOverdueRange,
      };
    case FooterButton.Past7Days:
      return {
        label: translate('date.pastXDays', { days: 7 }),
        getRange: (timeZone) => getPastXDaysRange(7, timeZone),
      };
    case FooterButton.Past14Days:
      return {
        label: translate('date.pastXDays', { days: 14 }),
        getRange: (timeZone) => getPastXDaysRange(14, timeZone),
      };
    case FooterButton.Past30Days:
      return {
        label: translate('date.pastXDays', { days: 30 }),
        getRange: (timeZone) => getPastXDaysRange(30, timeZone),
      };
    case FooterButton.Next7Days:
      return {
        label: translate('date.nextXDays', { days: 7 }),
        getRange: (timeZone) => getNextXDaysRange(7, timeZone),
      };
    case FooterButton.Next14Days:
      return {
        label: translate('date.nextXDays', { days: 14 }),
        getRange: (timeZone) => getNextXDaysRange(14, timeZone),
      };
    case FooterButton.Next30Days:
      return {
        label: translate('date.nextXDays', { days: 30 }),
        getRange: (timeZone) => getNextXDaysRange(30, timeZone),
      };
    case FooterButton.Today:
    default:
      return {
        label: translate('date.today'),
        getRange: getTodayRange,
      };
  }
};
